import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Drama } from "lucide-react";
import { api } from "../lib/api";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";

export const MOOD_AXES = [
  { key: "warmth", left: "Cold", right: "Warm" },
  { key: "confidence", left: "Shy", right: "Bold" },
  { key: "patience", left: "Short-fused", right: "Patient" },
  { key: "playfulness", left: "Serious", right: "Playful" },
  { key: "openness", left: "Guarded", right: "Open" },
  { key: "dominance", left: "Yielding", right: "Commanding" },
];

export const SPEECH_STYLES = [
  "Curt, few words", "Formal", "Flirty", "Sarcastic", "Swears a lot", "Soft-spoken",
  "Rambles", "Teasing", "Uses pet names", "Never says names", "Poetic", "Blunt",
];

export const EMPTY_DIRECTION = { enabled: true, notes: "", mood: { enabled: false, axes: [], values: {} }, speech: { enabled: false, styles: [], custom: "" } };
const EMPTY = EMPTY_DIRECTION;
const label = "font-sans-ui text-[9px] uppercase tracking-[0.25em] text-stone-500";

const MoodEditor = ({ mood, onChange }) => {
  const toggleAxis = (k) => {
    const on = mood.axes.includes(k);
    onChange({ ...mood, axes: on ? mood.axes.filter(a => a !== k) : [...mood.axes, k], values: { ...mood.values, [k]: mood.values[k] ?? 75 } });
  };
  return (
    <div className="space-y-2" data-testid="direction-mood-editor">
      <p className="font-serif-reading italic text-[11px] text-stone-400">Pick the moods to steer. A slider left in the middle has no effect.</p>
      {MOOD_AXES.map(a => {
        const on = mood.axes.includes(a.key);
        return (
          <div key={a.key} className="space-y-1">
            <button type="button" data-testid={`direction-mood-axis-${a.key}`} onClick={() => toggleAxis(a.key)}
              className={`font-sans-ui text-[10px] tracking-wide transition ${on ? "text-rose-300" : "text-stone-500 hover:text-stone-300"}`}>
              {on ? "●" : "○"} {a.left} ↔ {a.right}
            </button>
            {on && (
              <div className="flex items-center gap-2 pl-3">
                <span className="text-[10px] text-stone-400 w-16">{a.left}</span>
                <Slider data-testid={`direction-mood-slider-${a.key}`} min={0} max={100} step={5}
                  value={[mood.values[a.key] ?? 50]}
                  onValueChange={([v]) => onChange({ ...mood, values: { ...mood.values, [a.key]: v } })} className="flex-1" />
                <span className="text-[10px] text-stone-400 w-16 text-right">{a.right}</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const SpeechEditor = ({ speech, onChange }) => (
  <div className="space-y-2" data-testid="direction-speech-editor">
    <div className="flex flex-wrap gap-1.5">
      {SPEECH_STYLES.map(st => {
        const on = speech.styles.includes(st);
        return (
          <button key={st} type="button" data-testid={`direction-speech-${st.replace(/[^a-z]+/gi, "-").toLowerCase()}`}
            onClick={() => onChange({ ...speech, styles: on ? speech.styles.filter(x => x !== st) : [...speech.styles, st] })}
            className={`px-2 py-1 rounded-full border text-[10px] font-sans-ui transition ${on ? "border-rose-800 bg-rose-950/40 text-rose-200" : "border-stone-800 text-stone-500 hover:text-stone-200"}`}>
            {st}
          </button>
        );
      })}
    </div>
    <input data-testid="direction-speech-custom" value={speech.custom} onChange={(e) => onChange({ ...speech, custom: e.target.value })}
      placeholder="Anything else? e.g. drops into Portuguese when angry"
      className="w-full bg-stone-900 border border-stone-800 text-stone-200 font-serif-reading text-xs px-2 py-1.5 rounded-sm focus:outline-none focus:border-rose-800 placeholder:text-stone-600" />
  </div>
);

const Toggle = ({ testid, title, checked, onChange }) => (
  <label className="flex items-center justify-between gap-2">
    <span className={label}>{title}</span>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </label>
);

// Controlled field set (no saving) — used by the reader panel and the story setup wizard.
export const DirectionFields = ({ char, d, setD }) => (
  <>
    <Toggle testid="direction-enabled-switch" title="Use direction for this character" checked={d.enabled} onChange={(v) => setD({ ...d, enabled: v })} />
    <textarea data-testid="direction-notes-input" value={d.notes} onChange={(e) => setD({ ...d, notes: e.target.value })} rows={4}
      placeholder={`How does ${char.name.split(" ")[0]} act? e.g. When jealous, goes quiet and cold instead of yelling. Never apologises first. Softens only when touched.`}
      className="w-full bg-stone-900 border border-stone-800 text-stone-200 font-serif-reading text-sm px-3 py-2 rounded-sm focus:outline-none focus:border-rose-800 placeholder:text-stone-600" />
    <Toggle testid="direction-mood-switch" title="Mood sliders" checked={d.mood.enabled} onChange={(v) => setD({ ...d, mood: { ...d.mood, enabled: v } })} />
    {d.mood.enabled && <MoodEditor mood={d.mood} onChange={(mood) => setD({ ...d, mood })} />}
    <Toggle testid="direction-speech-switch" title="Speech style" checked={d.speech.enabled} onChange={(v) => setD({ ...d, speech: { ...d.speech, enabled: v } })} />
    {d.speech.enabled && <SpeechEditor speech={d.speech} onChange={(speech) => setD({ ...d, speech })} />}
  </>
);

export const withDirectionDefaults = (initial) => ({
  ...EMPTY, ...initial,
  mood: { ...EMPTY.mood, ...(initial?.mood || {}) },
  speech: { ...EMPTY.speech, ...(initial?.speech || {}) },
});

const DirectionEditor = ({ storyId, char, initial, onSaved }) => {
  const [d, setD] = useState(withDirectionDefaults(initial));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/stories/${storyId}/character-direction/${char.id}`, d);
      toast.success(`Direction saved for ${char.name.split(" ")[0]}`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save direction");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-3" data-testid={`direction-editor-${char.id}`}>
      <DirectionFields char={char} d={d} setD={setD} />
      <div className="flex justify-end">
        <button type="button" data-testid="direction-save-btn" onClick={save} disabled={saving}
          className="font-sans-ui text-[9px] uppercase tracking-[0.25em] text-rose-300 px-3 py-1.5 border border-rose-800 hover:bg-rose-950/40 disabled:opacity-40 transition">
          {saving ? "Saving…" : "Save direction"}
        </button>
      </div>
    </div>
  );
};

export const CharacterDirectionPanel = ({ storyId, aiChars, direction, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(aiChars[0]?.id || "");
  useEffect(() => {
    if (!aiChars.some(c => c.id === sel)) setSel(aiChars[0]?.id || "");
  }, [aiChars, sel]);
  if (!aiChars.length) return null;
  const current = aiChars.find(c => c.id === sel);
  const activeCount = aiChars.filter(c => {
    const d = direction?.[c.id];
    return d && d.enabled !== false && (d.notes || d.mood?.enabled || d.speech?.enabled);
  }).length;
  return (
    <div data-testid="direction-panel" className="border border-stone-800/60 bg-stone-900/30 rounded-2xl p-3 space-y-2">
      <button type="button" data-testid="direction-toggle" onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-1 py-1 text-stone-500 hover:text-rose-300 font-sans-ui text-[9px] uppercase tracking-[0.25em] transition">
        <span className="flex items-center gap-2">
          <Drama className="w-3 h-3" strokeWidth={1.5} />
          {open ? "▾" : "▸"} Character direction
        </span>
        <span className="text-stone-500 normal-case tracking-normal text-[10px]">{activeCount ? `${activeCount} directed` : "how they act"}</span>
      </button>
      {open && (
        <div className="space-y-3 pt-1 px-1">
          <div className="flex flex-wrap gap-1.5">
            {aiChars.map(c => (
              <button key={c.id} type="button" data-testid={`direction-char-${c.id}`} onClick={() => setSel(c.id)}
                className={`px-3 py-1 rounded-full border text-[11px] font-serif-reading transition ${sel === c.id ? "border-rose-800 bg-rose-950/40 text-rose-200" : "border-stone-800 text-stone-400 hover:text-stone-200"}`}>
                {c.name.split(" ")[0]}
              </button>
            ))}
          </div>
          {current && <DirectionEditor key={current.id} storyId={storyId} char={current} initial={direction?.[current.id]} onSaved={onChanged} />}
        </div>
      )}
    </div>
  );
};
